import { cn } from '@1trading/react/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';

import Header from '@/components/layout/header';
import { type InferredTheme, useTheme } from '@/context/theme';

const layoutVariants = cva(
  'flex flex-col items-stretch flex-1 w-full max-w-3xl mx-auto p-4 pt-[calc(var(--header-height-sm)+theme("spacing.4"))] md:pt-[calc(var(--header-height-md)+theme("spacing.20"))]',
  {
    variants: {
      fill: {
        false: '',
        true: '-md:max-h-screen -md:overflow-hidden',
      },
      size: {
        sm: 'max-w-xl',
        default: 'max-w-3xl',
        lg: 'max-w-4xl',
      },
    },
    defaultVariants: {
      fill: false,
      size: 'default',
    },
  },
);

interface BaseLayoutProps extends React.ComponentPropsWithoutRef<'main'>, React.PropsWithChildren<VariantProps<typeof layoutVariants>> {
  logoutButton?: React.ReactNode;
}

const THEME_BACKGROUND_IMAGES = {
  light: `${import.meta.env.VITE_APP_CDN_URL}/img/onetrading-icon-background-rebrand-light.svg`,
  dark: `${import.meta.env.VITE_APP_CDN_URL}/img/onetrading-icon-background-rebrand.svg`,
} satisfies Record<InferredTheme, string>;

const BaseLayout = ({ children, className, fill, logoutButton, size, ...props }: BaseLayoutProps) => {
  const { inferredTheme } = useTheme();

  return (
    <div
      className="flex flex-col min-h-screen -md:!bg-none md:bg-[size:33.33%_auto] md:bg-no-repeat md:bg-right-bottom"
      style={{ backgroundImage: `url(${THEME_BACKGROUND_IMAGES[inferredTheme]})` }}
    >
      <Header logoutButton={logoutButton} />
      <main className={cn(layoutVariants({ fill, size }), className)} {...props}>
        {children}
      </main>
    </div>
  );
};
BaseLayout.displayName = 'BaseLayout';

export default BaseLayout;
